import { ACCIDENT_FILES, AccidentUser, Car } from './accident';

export enum RENT_FILES {
  CONTRACT = 'carRentalContract',
  AMENDMENT = 'carRentalContractAmendment',
  HANDOVER_PROTOCOL = 'carRentalProtocolHandover',
  TAKEOVER_PROTOCOL = 'carRentalProtocolTakeover'
}

export enum RENT_SOURCES {
  USER = 'user',
  ACCIDENT = 'accident'
}

export interface GenerableDocument {
  id: number;
  template: RENT_FILES | ACCIDENT_FILES;
  filename?: string;
  path: string;
  isSigned?: boolean;
  isGenerable?: boolean;
  updatedDate?: string;
  requestSignature?: boolean;
  allowRegenerate?: boolean;
  allowPreview?: boolean;
}

export interface CarRental {
  id: number;
  isBlocked?: boolean;
  arrangeUserFullName?: string;
  rentNumber?: string;
  createdDate?: Date;
  rentalDate: Date;
  returnDate?: Date;
  reservationDate?: Date;
  dailyRate?: string;
  accidentId: number;
  isExpired?: boolean;
  isReturned: boolean;
  isRunning?: boolean;
  rentUserId?: number;
  returnUserId?: number;
  documents: GenerableDocument[];
  car: Car;
  user: AccidentUser;
  accident?: {
    id: number;
    contractNumber: string;
    date: string;
    dealerId: number;
    branchId: number;
  };
  note?: string;
}

export interface AvailableRentalCarParam {
  name: string;
  type: string;
  value: string;
  label: string;
}

export interface AvailableRentalCar {
  id: number;
  brand: string;
  model: string;
  color: string;
  vin: string;
  plateNumber: string;
  licenseId: string;
  manufacturedYear: string;
  branchId: number;
  carConditionParams: AvailableRentalCarParam[];
  dailyRate: string;
  rentalInConflict?: CarRental;
}

export interface CarBranch {
  id: number;
  title: string;
  address: {
    street: string;
    city: string;
    zip: string;
  };
  openingHours?: string;
  visibility: boolean;
  team: number;
}

export interface AvailableRentalCars {
  cars: AvailableRentalCar[];
  carParameters: AvailableRentalCarParam[];
  branches: CarBranch[];
  isBlock: boolean;
}

export interface CarRentalRequest {
  rentalDate?: Date;
  returnDate?: Date;
  branchId?: number;
  carParameters?: string[];
  searchPhrase?: string;
}

export interface RentCarRequest {
  rental: {
    rentalDate: Date;
    returnDate?: Date;
    carId?: number;
    dailyRate?: string;
    arrange: {
      type: string;
      userId: number;
    };
    forceApproval?: boolean;
  };
  source: {
    type: string;
    objectId?: number;
  };
}

export interface RentalTakeoverRequest {
  rental: {
    returnDate?: Date;
  };
  takeover: {
    type: string;
    userId: number;
    note?: string;
    branchId: number;
    carStatus: number;
  };
}

export interface RentCarUpdateRequest {
  rental: {
    rentalDate: Date;
    returnDate?: Date;
    dailyRate: string;
    note?: string;
  };
}

export interface DocumentSignRequest {
  filename: string;
  data: string;
  template: string;
}

interface RentalCarOption {
  title: string;
  value: string | number | null;
}

export interface RentalCarConditionParam {
  name: string;
  type: 'value' | 'option';
  value: string | number | null;
  label: string;
  options?: RentalCarOption[];
}

export interface RentalCar {
  id: number;
  brand: string;
  model: string;
  color: string;
  vin: string;
  plateNumber: string;
  licenseId: string;
  manufacturedYear: string;
  insuranceId: number;
  branchId: number;
  carConditionParams: RentalCarConditionParam[];
  dailyRate: string;
  carRentalStatus: number;
  note: string;
  stk: Date;
  vignette: string | null;
  tires: Date;
  warranty: string | null;
  motorization: string;
  gearbox: string;
  fuel: string;
  visibility: number;
  label: string;
  insurance: any[];
}

export interface RentalStartType {
  rental?: {
    rentalDate: Date;
    returnDate?: Date;
  };
}

export type CarRentalStatus = 'ongoing' | 'blocked' | 'completed';

export interface CarRentalsResponse {
  items: CarRental[];
  pagination: {
    total: number;
    pages: number;
    limit: number;
    offset: number;
  };
}
